import * as React from 'react';
import cn from 'classnames';

import {
  TypographySize,
  TypographyType,
  TypographyColor,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import { TranslationString } from 'shared/entities/localization';

import Typography from '../Typography';

import './Switch.modules.scss';

type Props = {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
  title?: TranslationString;
  size?: 's' | 'm';
  className?: string;
};

const Switch: React.FC<Props> = ({
  value,
  onChange,
  disabled,
  title,
  size = 'm',
  className
}) => {
  const titleId = React.useId();

  const handleChange = React.useCallback(() => {
    if (disabled) {
      return;
    }

    onChange(!value);
  }, [disabled, value, onChange]);

  const handleKeyDown = React.useCallback<
    React.KeyboardEventHandler<HTMLDivElement>
  >((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.currentTarget.click();
    }
  }, []);

  return (
    <div
      styleName={cn(
        'switch-container',
        disabled && 'switch-container_disabled'
      )}
      className={className}
      onClick={handleChange}
    >
      <div
        role="switch"
        aria-checked={value}
        aria-disabled={disabled}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={disabled ? -1 : 0}
        onKeyDown={handleKeyDown}
        styleName={cn(
          'switch',
          `switch_size_${size}`,
          value && 'switch_active',
          disabled && 'switch_disabled'
        )}
      >
        <div styleName={cn('switch__inner', `switch__inner_size_${size}`)} />
      </div>
      {title && (
        <Typography
          id={titleId}
          styleName="switch__title"
          fontType={TypographyType.text}
          fontSize={TypographySize.s}
          fontWeight={TypographyWeight.heavy}
          fontColor={TypographyColor.secondary}
        >
          {title}
        </Typography>
      )}
    </div>
  );
};

export default Switch;
